/**
 * useProvenanceSummaries — Batch hook for fetching lightweight provenance summaries.
 *
 * Designed for activity feeds and timelines where rendering provenance shields
 * inline would otherwise require N+1 full chain fetches.
 *
 * - Calls POST /api/v1/analytics/actions/provenance:summary
 * - De-duplicates IDs
 * - Returns Map<actionId, ProvenanceSummary> for O(1) lookup
 * - Tenant-aware cache key
 */

import type { ProvenanceSummary } from '../components/provenance-types';

const React = (globalThis as any).React;
const { useState, useEffect, useRef, useCallback, useMemo } = React;
const { usePlugin } = (globalThis as any).PluginSDK || {};

interface BatchCacheEntry {
  summaries: Map<string, ProvenanceSummary>;
  timestamp: number;
}

const CACHE_TTL_MS = 30_000;
const PENDING_CACHE_TTL_MS = 5_000;
const batchCache = new Map<string, BatchCacheEntry>();

export interface UseProvenanceSummariesResult {
  summaries: Map<string, ProvenanceSummary>;
  status: 'idle' | 'loading' | 'success' | 'error';
  error: string;
  refetch: () => void;
}

export function useProvenanceSummaries(
  actionIds: string[],
): UseProvenanceSummariesResult {
  const plugin = usePlugin?.() ?? null;
  const sdkApi = plugin?.sdk?.api ?? null;
  const tenantId: string = plugin?.context?.tenantId ?? 'default';

  const [summaries, setSummaries] = useState(new Map());
  const [status, setStatus] = useState(
    actionIds.length > 0 ? 'loading' : 'idle',
  );
  const [error, setError] = useState('');
  const mountedRef = useRef(true);

  const deduped = useMemo(() => [...new Set(actionIds)], [actionIds.join(',')]);
  const cacheKey = `${tenantId}:batch:${deduped.sort().join(',')}`;

  const fetchSummaries = useCallback(async () => {
    if (deduped.length === 0) {
      setStatus('idle');
      setSummaries(new Map());
      return;
    }

    const cached = batchCache.get(cacheKey);
    const now = Date.now();

    if (cached) {
      const hasPending = Array.from(cached.summaries.values()).some(
        (s) => s.status === 'pending',
      );
      const ttl = hasPending ? PENDING_CACHE_TTL_MS : CACHE_TTL_MS;
      if (now - cached.timestamp < ttl) {
        setSummaries(cached.summaries);
        setStatus('success');
        return;
      }
    }

    setStatus('loading');
    setError('');

    const resolveFetch = (): typeof globalThis.fetch => {
      if (sdkApi?.fetch) return sdkApi.fetch;
      return globalThis.fetch.bind(globalThis);
    };

    const doFetch = resolveFetch();

    try {
      const response = await doFetch(
        '/api/v1/analytics/actions/provenance:summary',
        {
          method: 'POST',
          credentials: 'include' as RequestCredentials,
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ action_ids: deduped }),
        },
      );

      if (!mountedRef.current) return;

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const data = await response.json();
      const map = new Map<string, ProvenanceSummary>();

      for (const s of data.summaries ?? []) {
        map.set(s.action_id, s);
      }

      batchCache.set(cacheKey, { summaries: map, timestamp: now });
      setSummaries(map);
      setStatus('success');
    } catch (err: any) {
      if (!mountedRef.current) return;
      setStatus('error');
      setError(err?.message ?? 'Failed to load provenance summaries');
    }
  }, [deduped, cacheKey, sdkApi]);

  useEffect(() => {
    mountedRef.current = true;
    fetchSummaries();
    return () => { mountedRef.current = false; };
  }, [fetchSummaries]);

  // Auto-refetch on shorter interval when any summary is in 'pending' state
  useEffect(() => {
    const hasPending = (Array.from(summaries.values()) as ProvenanceSummary[]).some(
      (s) => s.status === 'pending',
    );
    if (!hasPending || status !== 'success') return;

    const timer = setInterval(() => {
      if (mountedRef.current) fetchSummaries();
    }, PENDING_CACHE_TTL_MS);

    return () => clearInterval(timer);
  }, [summaries, status, fetchSummaries]);

  return { summaries, status, error, refetch: fetchSummaries };
}

export default useProvenanceSummaries;
